import React from 'react';
import { Link } from 'react-router-dom';
import { 
  HeartPulse, Stethoscope, Pill, TestTubes, Activity, Receipt, 
  FileHeart, Ambulance, ArrowRight, ChevronRight
} from 'lucide-react';
import Hospital from '../../assets/Hospital.png'; // Assuming you have an image in assets

const CONTAINER = 'w-full max-w-7xl xl:max-w-[95%] 2xl:max-w-[85vw] mx-auto px-6 sm:px-8 lg:px-8';
const THEME_COLOR = '#16a34a'; // Emerald theme

/* ============================== PAGE DATA ============================== */

const FEATURES = [
  { title: 'OPD & IPD', icon: Stethoscope, desc: 'Manage out-patient and in-patient departments, appointments, and ward allocations seamlessly.' },
  { title: 'Patient Management', icon: HeartPulse, desc: 'Maintain comprehensive patient records, medical histories, and treatment plans in a centralized database.' },
  { title: 'Pharmacy', icon: Pill, desc: 'Track medication inventory, manage prescriptions, and handle pharmacy billing efficiently.' },
  { title: 'Laboratory', icon: TestTubes, desc: 'Streamline sample tracking, test results generation, and automated report delivery to patients.' },
  { title: 'Radiology', icon: Activity, desc: 'Manage imaging requests, digital scans, and radiologist reports directly within the patient profile.' },
  { title: 'Billing & Insurance', icon: Receipt, desc: 'Automate invoicing, process insurance claims, and track payments across all hospital departments.' },
  { title: 'EMR', icon: FileHeart, desc: 'Secure Electronic Medical Records ensuring patient data is accessible to authorized staff instantly.' },
  { title: 'Ambulance', icon: Ambulance, desc: 'Track vehicle availability, manage driver schedules, and optimize emergency routing.' }
];

const HospitalERP = () => {
  return (
    <div className="overflow-x-hidden pt-0">
      <style>{`
        @keyframes seFadeUp { from{opacity:0;transform:translateY(24px)} to{opacity:1;transform:translateY(0)} }
        .se-fade-up { animation: seFadeUp .7s ease-out both; }
        @keyframes seFloat { 0%,100%{transform:translateY(0)} 50%{transform:translateY(-12px)} }
        .se-float { animation: seFloat 6s ease-in-out infinite; }
        @media (prefers-reduced-motion: reduce) { .se-float, .se-fade-up { animation: none !important; } }
      `}</style>

      {/* ================= HERO SECTION ================= */}
      <section className="relative overflow-hidden bg-gradient-to-b from-[#f0fdf4] via-[#f8fafc] to-white">
        <div className="pointer-events-none absolute -top-24 -right-24 w-96 h-96 rounded-full bg-primary/10 blur-3xl" />
        <div className="pointer-events-none absolute top-40 -left-24 w-80 h-80 rounded-full bg-[#16a34a]/10 blur-3xl" />
        
        <div className={`${CONTAINER} relative py-5 lg:py-6`}>
          <div className="grid lg:grid-cols-2 gap-8 lg:gap-8 items-center">
            
            {/* LEFT SIDE: Text Content */}
            <div className="max-w-2xl relative z-10 se-fade-up">
               <nav className="flex flex-wrap items-center gap-2 text-sm text-slate-500 mb-6">
                 <Link to="/" className="hover:text-primary transition-colors">Home</Link>
                    <ChevronRight className="w-3.5 h-3.5 text-slate-400" />
                 <Link to="/products" className="hover:text-primary transition-colors">Products</Link>
                    <ChevronRight className="w-3.5 h-3.5 text-slate-400" />
                <span className="text-primary font-medium">Hospital ERP</span>
               </nav>

              <span className="inline-flex items-center gap-2 text-xs font-bold tracking-[0.18em] uppercase text-primary bg-white ring-1 ring-primary/25 px-4 py-1.5 rounded-full mb-6">
                <HeartPulse className="w-4 h-4 text-[#16a34a]" /> SmartEdz Healthcare Solutions
              </span>
              <h1 className="text-4xl sm:text-5xl xl:text-[3.7rem] font-extrabold leading-[1.08] tracking-tight mb-6" style={{ color: '#13294b' }}>
                Intelligent Hospital ERP for Modern Healthcare
              </h1>
              <p className="text-lg text-slate-500 max-w-xl leading-relaxed mb-8">
                Streamline clinical workflows, enhance patient care, and optimize hospital administration with our comprehensive healthcare management system.
              </p>
              <div className="flex flex-wrap gap-4">
                <Link to="/request-demo" className="inline-flex items-center gap-2 rounded-lg px-7 py-3.5 font-semibold text-white shadow-lg shadow-primary/30 bg-primary hover:brightness-110 transition">
                  Request a Demo <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </div>

            {/* RIGHT SIDE: Image replacing mockups */}
            <div className="relative se-fade-up w-full" style={{ animationDelay: '.15s' }}>
              <div className="absolute inset-x-8 top-4 bottom-16 bg-gradient-to-tr from-[#16a34a]/15 to-[#4ade80]/15 blur-3xl rounded-[48px]" />
              <img 
                src={Hospital} 
                alt="Hospital ERP Dashboard Illustration" 
                className="relative z-10 w-full h-auto object-contain mix-blend-multiply hover:scale-[1.02] transition-transform duration-500"
              />
            </div>

          </div>
        </div>
      </section>

      {/* ================= CORE MODULES ================= */}
      <section className="bg-gradient-to-b from-[#f5f9fd] to-white py-14">
        <div className={CONTAINER}>
          <div className="text-center mb-10">
            <span className="block text-xs font-bold tracking-[0.2em] uppercase text-primary mb-3">Core Modules</span>
            <h2 className="text-3xl sm:text-4xl font-extrabold tracking-tight" style={{ color: '#13294b' }}>Everything you need in one dashboard</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {FEATURES.map((feature, index) => (
              <div key={index} className="group h-full flex flex-col bg-white rounded-2xl ring-1 ring-slate-100 p-6 shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all duration-300">
                <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-4 transition-transform group-hover:scale-110" style={{ backgroundColor: `${THEME_COLOR}1A` }}>
                  <feature.icon className="w-6 h-6" style={{ color: THEME_COLOR }} />
                </div>
                <h3 className="font-bold text-slate-800 text-lg mb-3">{feature.title}</h3>
                <p className="text-[13px] text-slate-500 leading-relaxed flex-1">{feature.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default HospitalERP;